import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";
import { ArrowRight, Brain, CloudRain, Activity, Puzzle } from "lucide-react";

const ANXIETY_URL = "https://forms.microsoft.com/Pages/ResponsePage.aspx?id=8b6jusaYg0KZdna1WthYyvuYhC2GEx5AjnpD96hZzgpUOVAxOEZHVDRMNU1aRldITVNLTzdERFAyUS4u";
const DEPRESSION_URL = "https://forms.microsoft.com/Pages/ResponsePage.aspx?id=8b6jusaYg0KZdna1WthYylUnFnfWUJlCna1gDqSCsONUNjNVSzU5QUU4SVNEUFZLUUNDRlNQM0NWTSQlQCN0PWcu";
const ADHD_URL = "https://forms.microsoft.com/Pages/ResponsePage.aspx?id=8b6jusaYg0KZdna1WthYylUnFnfWUJlCna1gDqSCsONUOU5KTDBTNVZJNFFaWjlUU0xXMEk1TzI1OSQlQCN0PWcu";

const STONES_IMG = "https://static.prod-images.emergentagent.com/jobs/45c13cc0-c545-4ce4-a5ad-55fc17f653c4/images/2361b952b27646f7c839c2b0e62e559f579924ebb54897bd6c5ddea8060e6120.png";

const specialties = [
  {
    icon: Activity,
    name: "Anxiety",
    desc: "Constant worry, racing thoughts, panic, or feeling 'on edge' can be overwhelming. We'll help you calm your body and regain control.",
    long: "We work with adults experiencing generalized anxiety, social anxiety, panic, and high-functioning anxiety. Through CBT, exposure work, and somatic regulation tools, we help you reduce the daily charge and rebuild trust in yourself.",
    quickTest: "Take a 2-minute anxiety check.",
    url: ANXIETY_URL,
    image: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=900&q=80",
  },
  {
    icon: CloudRain,
    name: "Depression",
    desc: "Feeling numb, low, or exhausted most days can be more than stress. Therapy can help you lift the fog and rebuild routines and hope.",
    long: "Whether you're navigating a depressive episode or a long stretch of low mood, we'll help you understand its roots, rebuild gentle daily structure, and reconnect with what matters to you.",
    quickTest: "Take a 2-minute depression check.",
    url: DEPRESSION_URL,
    image: "https://images.unsplash.com/photo-1517299321609-52687d1bc55a?w=900&q=80",
  },
  {
    icon: Brain,
    name: "ADHD",
    desc: "Struggling with focus, procrastination, overwhelm, or emotional reactivity? Learn tools for attention, organization, and follow-through.",
    long: "ADHD shows up differently for everyone. We combine evidence-based skills coaching with therapy to address the executive function challenges, emotional reactivity, and self-worth wounds that often accompany an ADHD brain.",
    quickTest: "Take a quick ADHD screener.",
    url: ADHD_URL,
    image: "https://images.unsplash.com/photo-1485628390555-1a7bd3c2f3b4?w=900&q=80",
  },
  {
    icon: Puzzle,
    name: "Autism & Life Skills",
    desc: "Neurodivergent-affirming therapy and coaching for autistic adults — focused on the daily skills that build independence, confidence, and connection.",
    long: "We support autistic adults across the spectrum with practical life-skills coaching: executive function, sensory regulation, social scripts, workplace navigation, communication, routines, and emotional self-advocacy. Our work is identity-affirming — we help you build a life that fits your wiring, not one that masks it. We also support late-diagnosed adults processing what an autism identification means for their story.",
    quickTest: "Read more about our approach.",
    url: "/autism-life-skills",
    image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=900&q=80",
    internal: true,
  },
];

export default function Specialties() {
  return (
    <div data-testid="specialties-page">
      <SEO
        title="Specialties — Anxiety, Depression, ADHD & Autism Support"
        description="Therapy and life-skills coaching for adults across Texas. Specialties include anxiety, depression, ADHD, and autism — with neurodivergent-affirming care."
        path="/specialties"
      />
      <PageHeader
        overline="What We Treat"
        title={<>Specialties &amp; <em className="italic text-terracotta">Self-Checks.</em></>}
        subtitle="We specialize in supporting adults through anxiety, depression, ADHD, and the patterns that quietly shape daily life. Start with a quick self-check to clarify how you're feeling."
        image={STONES_IMG}
      />

      <section className="py-16 md:py-24 bg-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-16 md:space-y-24">
          {specialties.map((s, i) => {
            const Icon = s.icon;
            const flip = i % 2 === 1;
            return (
              <article
                key={s.name}
                data-testid={`specialty-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
              >
                <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-sage/30">
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
                  <div className="h-12 w-12 rounded-full bg-forest/5 flex items-center justify-center text-forest mb-6">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <p className="overline mb-3">Specialty 0{i + 1}</p>
                  <h2 className="font-display text-4xl md:text-6xl text-forest font-medium leading-[1.05] tracking-tight">
                    {s.name}
                  </h2>
                  <p className="mt-6 text-lg text-ink/80 leading-relaxed">{s.desc}</p>
                  <p className="mt-4 text-ink/70 leading-relaxed">{s.long}</p>
                  <div className="mt-8 bg-surface border border-sage/25 rounded-2xl p-6 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
                    <div>
                      <p className="font-display text-xl text-forest font-medium">{s.internal ? "Learn More" : "Quick Test"}</p>
                      <p className="text-ink/70 text-sm mt-1">{s.quickTest}</p>
                    </div>
                    <a
                      href={s.url}
                      {...(s.internal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                      data-testid={`selfcheck-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-btn`}
                      className="inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-6 py-3 text-sm font-medium transition-colors shrink-0"
                    >
                      {s.internal ? "Get in touch" : "Self Check"} <ArrowRight size={14} />
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
